'use client'

import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from '@/hooks/use-debounced-callback'
import type { EmailCategoryGroup } from '@/lib/email-category/groups'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import {
  PUBLIC_TEMPLATE_SEARCH_PATH,
  TEMPLATE_SEARCH_DEBOUNCE_MS,
  TEMPLATE_SEARCH_MIN_QUERY_LENGTH,
} from '@/lib/templates/search-constants'
import { useTemplateSearchQuery } from '@/lib/templates/search-query-state'
import type { TemplateSearchResult } from '@/lib/templates/types'

export type PublicTemplateSearchState = {
  hits: ReadonlyArray<TemplateSearchResult>
  /** Query whose results are currently held (may differ while replacing). */
  resultQuery: string | null
  isStale: boolean
  isSearching: boolean
  isDisabled: boolean
  error: string | null
  retry: () => void
}

type SearchResponse<T> = {
  hits?: Array<T>
  disabled?: boolean
  error?: string
}

/** Why a search failed; its message is chosen at render, in the reader's language. */
type SearchFailure =
  | { kind: 'rateLimited'; retryAfter: string | null }
  | { kind: 'failed' }
  | { kind: 'thrown'; error: unknown }

/** A response the search answered with a failure (429, 5xx, a malformed body). */
class SearchResponseError extends Error {
  readonly failure: SearchFailure

  constructor(failure: SearchFailure) {
    super(failure.kind)
    this.name = 'SearchResponseError'
    this.failure = failure
  }
}

/**
 * Fetches public ANN results for a committed query. Empty query skips
 * the network. `{ disabled: true }` is cached for the session so local
 * / CI does not keep paying for a known-off index. Its error message is
 * in the reader's language (`templates` catalog), so a page that shows it
 * lists that namespace in its `ClientMessages`.
 */
export function usePublicTemplateSearch({
  query,
  limit,
  category,
  group,
  brandDomain,
  keepPreviousResults = false,
}: {
  query: string
  limit: number
  category?: string
  /** A browse group, sent apart from `category` (`templateTypeFilter`). */
  group?: EmailCategoryGroup
  brandDomain?: string
  /** Canvas callers retain painted results and gate activation separately. */
  keepPreviousResults?: boolean
}): PublicTemplateSearchState {
  return usePublicSearchRequest<TemplateSearchResult>({
    path: PUBLIC_TEMPLATE_SEARCH_PATH,
    query,
    limit,
    category,
    group,
    brandDomain,
    keepPreviousResults,
  })
}

function usePublicSearchRequest<T>({
  path,
  query,
  limit,
  category,
  group,
  brandDomain,
  keepPreviousResults,
}: {
  path: string
  query: string
  limit: number
  category?: string
  group?: EmailCategoryGroup
  brandDomain?: string
  keepPreviousResults: boolean
}): {
  hits: ReadonlyArray<T>
  resultQuery: string | null
  isStale: boolean
  isSearching: boolean
  isDisabled: boolean
  error: string | null
  retry: () => void
} {
  const [result, setResult] = useState<{
    hits: ReadonlyArray<T>
    query: string | null
    key: string | null
  }>({ hits: [], query: null, key: null })
  const [isSearching, setIsSearching] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const t = useTranslations('templates')
  const [failure, setFailure] = useState<{
    key: string
    failure: SearchFailure
  } | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)
  const abortRef = useRef<AbortController | null>(null)
  const trimmed = query.trim()
  const requestKey = JSON.stringify([
    path,
    trimmed,
    limit,
    category,
    group,
    brandDomain,
    retryNonce,
  ])
  const retry = useCallback(() => {
    setFailure(null)
    setIsSearching(true)
    setRetryNonce((nonce) => nonce + 1)
  }, [])

  useEffect(() => {
    abortRef.current?.abort()
    if (!trimmed) {
      setIsSearching(false)
      setFailure(null)
      // A cleared query has left this request. Re-entering it must stay
      // pending until a fresh response accepts the retained cached hits.
      setResult((previous) =>
        previous.key === null ? previous : { ...previous, key: null }
      )
      return
    }
    if (isDisabled) {
      setIsSearching(false)
      return
    }

    const controller = new AbortController()
    abortRef.current = controller
    setIsSearching(true)
    setFailure(null)

    const body: {
      query: string
      limit: number
      category?: string
      group?: EmailCategoryGroup
      brandDomain?: string
    } = { query: trimmed, limit }
    if (category) {
      body.category = category
    }
    if (group) {
      body.group = group
    }
    if (brandDomain) {
      body.brandDomain = brandDomain
    }

    void fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 429) {
          throw new SearchResponseError({
            kind: 'rateLimited',
            retryAfter: res.headers.get('Retry-After'),
          })
        }
        if (!res.ok) {
          throw new SearchResponseError({ kind: 'failed' })
        }
        return (await res.json()) as SearchResponse<T>
      })
      .then((data) => {
        if (controller.signal.aborted) {
          return
        }
        if (data.disabled) {
          setIsDisabled(true)
          return
        }
        if (!Array.isArray(data.hits)) {
          throw new SearchResponseError({ kind: 'failed' })
        }
        setResult({ hits: data.hits, query: trimmed, key: requestKey })
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err as Error).name === 'AbortError') {
          return
        }
        setFailure({
          key: requestKey,
          failure:
            err instanceof SearchResponseError
              ? err.failure
              : { kind: 'thrown', error: err },
        })
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsSearching(false)
        }
      })
    return () => controller.abort()
  }, [
    brandDomain,
    category,
    group,
    isDisabled,
    limit,
    path,
    trimmed,
    requestKey,
  ])

  useEffect(
    () => () => {
      abortRef.current?.abort()
    },
    []
  )

  const isStale = Boolean(trimmed) && !isDisabled && result.key !== requestKey
  const error =
    failure?.key === requestKey ? describeFailure(t, failure.failure) : null
  return {
    hits:
      !trimmed || isDisabled || (isStale && !keepPreviousResults)
        ? []
        : result.hits,
    resultQuery: trimmed && !isDisabled ? result.query : null,
    isStale,
    // Query changes must report pending immediately, before passive effects.
    isSearching:
      Boolean(trimmed) && !isDisabled && (isSearching || (isStale && !error)),
    isDisabled,
    error,
    retry,
  }
}

/**
 * Local draft + nuqs `q` for the search box that feeds `usePublicTemplateSearch`.
 * Typing is immediate; the URL (and therefore the fetch) commits after
 * debounce, on Enter, or on clear.
 */
export function useDebouncedTemplateSearchQuery() {
  const [query, setQuery] = useTemplateSearchQuery()
  const [draft, setDraft] = useState(query)

  useEffect(() => {
    setDraft(query)
  }, [query])

  const commit = useCallback(
    (next: string) => {
      const trimmed = next.trim()
      setDraft(trimmed)
      void setQuery(trimmed)
    },
    [setQuery]
  )

  const debouncedCommit = useDebouncedCallback(
    commit,
    TEMPLATE_SEARCH_DEBOUNCE_MS
  )

  const onDraftChange = useCallback(
    (next: string) => {
      setDraft(next)
      const trimmed = next.trim()
      if (
        trimmed.length === 0 ||
        trimmed.length >= TEMPLATE_SEARCH_MIN_QUERY_LENGTH
      ) {
        debouncedCommit(trimmed)
        return
      }
      debouncedCommit.cancel()
    },
    [debouncedCommit]
  )

  const onSubmit = useCallback(() => {
    debouncedCommit.cancel()
    commit(draft)
  }, [commit, debouncedCommit, draft])

  const onClear = useCallback(() => {
    debouncedCommit.cancel()
    setDraft('')
    void setQuery('')
  }, [debouncedCommit, setQuery])

  return { draft, query, onDraftChange, onSubmit, onClear }
}

/** A failed search's message in the reader's language. */
function describeFailure(
  t: ReturnType<typeof useTranslations<'templates'>>,
  failure: SearchFailure
): string {
  if (failure.kind === 'rateLimited') {
    return failure.retryAfter
      ? t('search.errors.rateLimitedRetry', { seconds: failure.retryAfter })
      : t('search.errors.rateLimited')
  }
  if (failure.kind === 'failed') {
    return t('search.errors.failed')
  }
  // A network or parse error keeps its own message, as before.
  return getErrorMessage(failure.error, t('search.errors.failed'))
}
